import { HStack } from "@chakra-ui/react";
import { offeringHasNoAccreditationStep, offeringIsTypeRegA } from "@equidefi/shared";
import type { DTO } from "@equidefi/shared/types/dto";
import { Heading, Paper, Text } from "@equidefi/ui";
import React, { useState, useMemo } from "react";
import { InvestmentClosed } from "../InvestmentProgress/Closed";
import { InvestmentProgressItem } from "../InvestmentProgress/ProgressItem";
import { InvestmentRejected } from "../InvestmentProgress/Rejected";
import {
  type InvestmentStep,
  investmentStepsCopy,
  pendingStepsAccreditation,
  pendingStepsNoAccreditation,
  postPendingStepsAccreditation,
  postPendingStepsNoAccreditation,
} from "./investmentSteps";

interface InvestmentProgressV2Props {
  investment: DTO.Investment | null | undefined;
  offering: DTO.Offering | null | undefined;
  children?: React.ReactNode;
}

/**
 * InvestmentProgressV2 - Enhanced progress display with phase separation
 *
 * This component displays different progress bars based on investment status:
 * - PENDING status: Shows investor workflow steps (PROFILE → ACCREDITATION)
 * - POST-PENDING status: Shows processing steps (DILIGENCE → CLOSING)
 * - CLOSED/CANCELED: Shows completion/rejection banners
 *
 * @param investment - Investment object with status and next step
 * @param offering - Offering object to determine step configuration
 * @param children - Optional children to render below progress bar
 */
const InvestmentProgressV2: React.FC<InvestmentProgressV2Props> = ({ investment, offering, children }) => {
  const [activeStep, setActiveStep] = useState<string>(investment?.next || "");

  if (!offering || !investment) return null;

  if (investment.status === "CLOSED") return <InvestmentClosed />;
  if (investment.status === "CANCELED") return <InvestmentRejected />;

  const offeringIsRegA = offeringIsTypeRegA(offering);
  const hasNoAccreditation = offeringHasNoAccreditationStep(offering);
  const isPending = investment.status === "PENDING";

  // Determine which steps to show based on investment status and offering type
  const steps = useMemo<readonly InvestmentStep[]>(() => {
    if (isPending) {
      return hasNoAccreditation ? pendingStepsNoAccreditation : pendingStepsAccreditation;
    }
    return hasNoAccreditation ? postPendingStepsNoAccreditation : postPendingStepsAccreditation;
  }, [hasNoAccreditation, isPending]);

  const stepsList = useMemo(
    () =>
      steps.map((step) => {
        return (
          <InvestmentProgressItem
            key={step}
            next={investment.next}
            step={step}
            active={activeStep === step}
            onClick={() => setActiveStep(step)}
            steps={steps as readonly string[]}
          />
        );
      }),
    [steps, investment.next, activeStep],
  );

  const stepCopy = investmentStepsCopy[activeStep as InvestmentStep];

  return (
    <Paper p="4" boxShadow="card">
      <Text opacity="70%" textTransform="uppercase" mb="5">
        Investment Progress
      </Text>
      <Heading fontWeight="bold" m="0">
        {stepCopy?.heading(offeringIsRegA) || ""}
      </Heading>
      <Text mb="5">{stepCopy?.text(offeringIsRegA) || ""}</Text>

      <HStack gap={{ base: 1, sm: 2 }} minW="full">
        {stepsList}
      </HStack>

      {children}
    </Paper>
  );
};

export default InvestmentProgressV2;
